import { useEffect } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown, type DropdownItem } from "@/components/sidebar/Dropdown";
import { ActiveTooltip } from "@/components/tooltip/Tooltip";
import { twMerge } from "@/utils/twMerge";

import type { MarimoApi } from "@/api";
import type { MarimoSessionStatus } from "@/components/marimo/types";
import type { DocumentModel } from "@/models/DocumentModel";

type Props = {
  doc: DocumentModel;
  isCollapsed?: boolean;
};

const DOT_BG_BY_STATUS: Record<MarimoSessionStatus, string> = {
  running: "bg-green-500",
  starting: "bg-yellow-500 animate-pulse",
  disconnected: "bg-gray-400",
  stopped: "bg-gray-400",
  error: "bg-red-500",
};

export const USAGE_POLL_INTERVAL_MS = 30_000;
const BYTES_PER_GB = 1024 ** 3;

export const MarimoStatusBar = observer(function MarimoStatusBar({ doc, isCollapsed }: Props) {
  const t = useTranslations("sidebar.marimo");
  const { sessionStatus, usageStats } = doc.marimo;
  const isSessionLive = sessionStatus === "running" || sessionStatus === "starting";
  const hasBusyCell = Array.from(doc.marimo.cellStatuses.values()).some(
    (s) => s === "running" || s === "queued",
  );
  const isRunAllBusy = sessionStatus === "starting" || hasBusyCell;

  useEffect(() => {
    if (sessionStatus !== "running") return;

    doc.marimo.refreshUsage();
    const pollIntervalId = window.setInterval(
      () => doc.marimo.refreshUsage(),
      USAGE_POLL_INTERVAL_MS,
    );
    return () => window.clearInterval(pollIntervalId);
  }, [sessionStatus, doc.marimo]);

  const handleRunAll = () => {
    doc.marimo.runCells();
  };

  const moreMenuItems: DropdownItem[] = [
    {
      title: t("stopKernel"),
      disabled: !isSessionLive,
      onClick: () => {
        doc.marimo.stopKernel();
      },
    },
  ];

  return (
    <div
      className={twMerge(
        "flex items-center gap-2.5 rounded-8 p-1.25 bg-gray-100 dark:bg-gray-900",
        isCollapsed && "flex-col",
      )}
    >
      <button
        type="button"
        aria-label={t("runAll")}
        aria-busy={isRunAllBusy}
        disabled={isRunAllBusy}
        onClick={handleRunAll}
        className={twMerge(
          "flex items-center justify-center gap-1 rounded-8 text-sm tracking-tight pl-2 pr-4 h-8 cursor-pointer",
          "border border-white dark:border-white/20",
          "bg-blue-100 dark:bg-blue-900 text-blue-500 dark:text-blue-200",
          "hover:bg-blue-200 dark:hover:bg-blue-800 focus:bg-blue-200 dark:focus:bg-blue-800",
          "disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-blue-100 dark:disabled:hover:bg-blue-900",
          isCollapsed && "h-7 w-7 px-0",
        )}
      >
        <Icon name="RunAllCellsAccented" />
        {!isCollapsed && <span>{t("runAll")}</span>}
      </button>
      <div
        role="status"
        aria-label={t(`sessionStatus.${sessionStatus}`)}
        title={t(`sessionStatus.${sessionStatus}`)}
        className={twMerge(
          "w-6 h-6 rounded-full shrink-0 flex items-center justify-center",
          "bg-white shadow-sm",
        )}
      >
        <span className={twMerge("w-3.5 h-3.5 rounded-full", DOT_BG_BY_STATUS[sessionStatus])} />
      </div>
      {sessionStatus === "running" && usageStats !== null && (
        <ActiveTooltip
          as="div"
          title={
            <UsagePopup
              sessionStatus={sessionStatus}
              hasBusyCell={hasBusyCell}
              usageStats={usageStats}
            />
          }
          className="flex items-center gap-1 text-[12px] tracking-tight text-gray-600 dark:text-gray-400 tabular-nums"
        >
          <Icon name="marimo/Memory" />
          {!isCollapsed && (
            <span>
              <span className="text-black dark:text-white">
                {(usageStats.memory.used / BYTES_PER_GB).toFixed(2)}
              </span>
              {t("units.gb")}
            </span>
          )}
        </ActiveTooltip>
      )}
      <Dropdown
        className="ml-auto"
        items={moreMenuItems}
        anchor={{ to: "top start", padding: "10px" }}
        toggleButtonComponent={
          <Sidebar.Button
            as="div"
            icon={<Icon name="More" />}
            title={t("more")}
            className={twMerge(
              "w-fit h-fit hover:bg-blue-100 hover:dark:bg-blue-900",
              isCollapsed ? "min-h-0 rounded-4" : "rounded-8",
            )}
            wrapperClassName={twMerge(isCollapsed && "p-0")}
          />
        }
      />
    </div>
  );
});

function UsagePopup({
  sessionStatus,
  hasBusyCell,
  usageStats,
}: {
  sessionStatus: MarimoSessionStatus;
  hasBusyCell: boolean;
  usageStats: MarimoApi.MarimoUsage;
}) {
  const t = useTranslations("sidebar.marimo");
  const kernelLabel =
    sessionStatus === "running"
      ? `${t("sessionStatus.running")} (${t(hasBusyCell ? "kernelBusy" : "kernelIdle")})`
      : t(`sessionStatus.${sessionStatus}`);
  const memTotal = (usageStats.memory.total / BYTES_PER_GB).toFixed(2);
  const memUsed = (usageStats.memory.used / BYTES_PER_GB).toFixed(2);

  return (
    <div className="flex flex-col gap-2 text-left text-xs whitespace-nowrap [--color-icon-bg:white]">
      <div className="flex items-center gap-2">
        <span className="w-5 h-5 shrink-0 flex items-center justify-center">
          <span
            className={twMerge(
              "w-3 h-3 rounded-full ring-3 ring-white",
              DOT_BG_BY_STATUS[sessionStatus],
            )}
          />
        </span>
        <span>
          {t("kernel")}: {kernelLabel}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Icon name="marimo/MemorySmall" />
        <span>
          {t("memory")}: {memUsed}/{memTotal}
          {t("units.gb")}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Icon name="marimo/CpuSmall" />
        <span>
          {t("cpu")}: {usageStats.cpu.percent.toFixed(0)}%
        </span>
      </div>
    </div>
  );
}
